import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack, Block, Download, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { trainingManagementService, Certificate } from '../services/trainingManagementService';

export default function CertificatesManagementPage() {
  const navigate = useNavigate();

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trainingManagementService.getCertificates({ limit: 100 });
      setCertificates(data.certificates);
    } catch (err: any) {
      setError(err.message || 'Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setRevokeModalOpen(true);
  };

  const confirmRevoke = async () => {
    if (!selectedCertificate || !revokeReason.trim()) {
      alert('Please provide a reason for revocation');
      return;
    }

    setRevoking(true);
    try {
      await trainingManagementService.revokeCertificate(selectedCertificate.id, revokeReason);
      await loadCertificates();
      setRevokeModalOpen(false);
      setSelectedCertificate(null);
      setRevokeReason('');
      alert('Certificate revoked successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to revoke certificate');
    } finally {
      setRevoking(false);
    }
  };

  // Filter certificates
  const filteredCertificates = certificates.filter((cert) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      cert.certificateNumber.toLowerCase().includes(query) ||
      cert.courseName.toLowerCase().includes(query) ||
      cert.user?.email.toLowerCase().includes(query) ||
      `${cert.user?.firstName} ${cert.user?.lastName}`.toLowerCase().includes(query)
    );
  });

  const stats = {
    total: certificates.length,
    active: certificates.filter(c => !c.isRevoked).length,
    revoked: certificates.filter(c => c.isRevoked).length,
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/training')} sx={{ mb: 2 }}>
          Back to Training Management
        </Button>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" fontWeight="bold">
            Certificate Management
          </Typography>
          <Button startIcon={<Refresh />} variant="outlined" onClick={loadCertificates}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="h4" fontWeight="bold">
              {stats.total}
            </Typography>
            <Typography variant="body2">Total Certificates</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, bgcolor: 'success.main', color: 'white' }}>
            <Typography variant="h4" fontWeight="bold">
              {stats.active}
            </Typography>
            <Typography variant="body2">Active</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, bgcolor: 'error.main', color: 'white' }}>
            <Typography variant="h4" fontWeight="bold">
              {stats.revoked}
            </Typography>
            <Typography variant="body2">Revoked</Typography>
          </Paper>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <TextField
        fullWidth
        label="Search certificates"
        placeholder="Search by certificate number, course name, or caregiver"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
      />

      {/* Certificates Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Certificate #</TableCell>
              <TableCell>Caregiver</TableCell>
              <TableCell>Course</TableCell>
              <TableCell>Issue Date</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCertificates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No certificates found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCertificates.map((cert) => (
                <TableRow key={cert.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                      {cert.certificateNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {cert.user?.firstName} {cert.user?.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cert.user?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{cert.courseName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(cert.completionDate).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {cert.expiryDate
                        ? new Date(cert.expiryDate).toLocaleDateString()
                        : 'No expiry'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {cert.isRevoked ? (
                      <Chip label="Revoked" size="small" color="error" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <IconButton size="small" title="Download">
                        <Download fontSize="small" />
                      </IconButton>
                      {!cert.isRevoked && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRevoke(cert)}
                          title="Revoke"
                        >
                          <Block fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Revoke Modal */}
      <Dialog open={revokeModalOpen} onClose={() => !revoking && setRevokeModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Revoke Certificate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to revoke this certificate?
          </Typography>

          {selectedCertificate && (
            <Box bgcolor="grey.100" p={2} borderRadius={1} my={2}>
              <Typography variant="body2" fontWeight="bold">
                {selectedCertificate.certificateNumber}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedCertificate.courseName}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Revocation"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            required
            sx={{ mt: 2 }}
            placeholder="Explain why this certificate is being revoked..."
          />

          <Alert severity="warning" sx={{ mt: 2 }}>
            This will remove the certificate from the caregiver's profile
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeModalOpen(false)} disabled={revoking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmRevoke}
            disabled={revoking || !revokeReason.trim()}
          >
            {revoking ? 'Revoking...' : 'Revoke Certificate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

